import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface Note {
  id: string;
  title: string;
  subtitle?: string;
  content: string;
  folderId?: string;
  tags: string[];
  attachments: Attachment[];
  isFavorite: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface Folder {
  id: string;
  name: string;
  parentId?: string;
  color: string;
  icon: string;
  createdAt: Date;
}

export interface Attachment {
  id: string;
  type: 'document' | 'case' | 'file';
  referenceId?: string;
  fileName: string;
  filePath?: string;
}

interface NotesManagerContextType {
  notes: Note[];
  folders: Folder[];
  addNote: (note: Omit<Note, 'id' | 'createdAt' | 'updatedAt'>) => Note;
  updateNote: (id: string, updates: Partial<Note>) => void;
  deleteNote: (id: string) => void;
  addFolder: (folder: Omit<Folder, 'id' | 'createdAt'>) => void;
  updateFolder: (id: string, updates: Partial<Folder>) => void;
  deleteFolder: (id: string) => void;
  moveNoteToFolder: (noteId: string, folderId?: string) => void;
  searchNotes: (query: string) => Note[];
  getNotesInFolder: (folderId?: string) => Note[];
  getFavoriteNotes: () => Note[];
  getRecentNotes: () => Note[];
}

const NotesManagerContext = createContext<NotesManagerContextType | undefined>(undefined);

// Sample data following the elegant design patterns
const initialFolders: Folder[] = [
  {
    id: "f1",
    name: "Case Analysis",
    color: "#3B82F6",
    icon: "folder",
    createdAt: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
  },
  {
    id: "f2",
    name: "Investigation Notes",
    color: "#EF4444", 
    icon: "search",
    createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000)
  },
  {
    id: "f3",
    name: "Reports & Findings",
    color: "#10B981",
    icon: "file-text",
    createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)
  }
];

const initialNotes: Note[] = [
  {
    id: "n1",
    title: "Medicare Fraud Pattern Analysis",
    content: `# Key Findings

## Overview
Investigation into suspicious billing patterns revealing potential Medicare fraud scheme involving coordinated overbilling across multiple providers.

## Evidence Summary
- **Billing Anomalies**: 347% increase in DME claims from target providers
- **Geographic Clustering**: Unusual concentration in Miami-Dade region
- **Temporal Patterns**: Spike correlates with new provider registrations

## Recommended Actions
1. Initiate formal audit of flagged providers
2. Cross-reference with NPPES database for verification
3. Coordinate with CMS for additional data analysis

## Risk Assessment
**Fraud Likelihood**: 94.2%
**Estimated Loss**: $2.3M - $4.7M
**Provider Count**: 12 high-risk entities`,
    folderId: "f1",
    tags: ["medicare", "fraud", "dme", "high-priority"],
    attachments: [
      {
        id: "a1",
        type: "case",
        referenceId: "case-2024-001",
        fileName: "Medicare Claims Analysis"
      }
    ],
    isFavorite: true,
    createdAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000),
    updatedAt: new Date(Date.now() - 1 * 60 * 60 * 1000)
  },
  {
    id: "n2", 
    title: "Provider Network Investigation",
    content: `# Network Analysis Report

## Methodology
Utilized graph analysis algorithms to identify potential collusive networks among healthcare providers showing suspicious billing coordination.

## Key Discoveries
- **Shell Companies**: 8 entities with minimal staff but high claim volumes
- **Shared Infrastructure**: Common addresses and phone numbers across "independent" practices
- **Billing Synchronicity**: Coordinated submission patterns suggesting centralized management

## Technical Details
- Graph density: 0.73 (highly connected network)
- Clustering coefficient: 0.89 (strong community structure)
- Centrality measures identify 3 key orchestrators

*Investigation ongoing - additional surveillance recommended.*`,
    folderId: "f2",
    tags: ["network-analysis", "investigation", "provider-fraud"],
    attachments: [],
    isFavorite: false,
    createdAt: new Date(Date.now() - 4 * 24 * 60 * 60 * 1000),
    updatedAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000)
  },
  {
    id: "n3",
    title: "Weekly Audit Summary",
    content: `# Weekly Performance Summary

## Cases Processed
- **Total Claims Reviewed**: 12,847
- **Fraud Cases Identified**: 23
- **Estimated Savings**: $847,392

## Model Performance
- **Accuracy**: 96.8% ↑ 2.1%
- **False Positive Rate**: 3.2% ↓ 0.8%
- **Processing Time**: Avg 2.3ms per claim

## Team Achievements
Outstanding work by the fraud detection team this week. Special recognition to Dr. Sarah Chen for identifying the complex Medicare Part B scheme that saved an estimated $2.3M.

## Next Week Goals
- Complete provider risk reassessment
- Deploy updated ML model v3.2
- Finalize Q4 compliance report`,
    folderId: "f3",
    tags: ["weekly-report", "performance", "team"],
    attachments: [
      {
        id: "a2",
        type: "document",
        fileName: "Q4_Performance_Metrics.pdf",
        filePath: "/documents/q4-metrics.pdf"
      }
    ],
    isFavorite: true,
    createdAt: new Date(Date.now() - 1 * 24 * 60 * 60 * 1000),
    updatedAt: new Date(Date.now() - 30 * 60 * 1000)
  }
];

export function NotesManagerProvider({ children }: { children: ReactNode }) {
  const [notes, setNotes] = useState<Note[]>(initialNotes);
  const [folders, setFolders] = useState<Folder[]>(initialFolders);

  const addNote = (noteData: Omit<Note, 'id' | 'createdAt' | 'updatedAt'>): Note => {
    const newNote: Note = {
      ...noteData,
      id: `note-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date(),
      updatedAt: new Date()
    };
    setNotes(prev => [newNote, ...prev]);
    return newNote;
  };

  const updateNote = (id: string, updates: Partial<Note>) => {
    setNotes(prev => prev.map(note => 
      note.id === id 
        ? { ...note, ...updates, updatedAt: new Date() }
        : note
    ));
  };

  const deleteNote = (id: string) => {
    setNotes(prev => prev.filter(note => note.id !== id));
  };

  const addFolder = (folderData: Omit<Folder, 'id' | 'createdAt'>) => {
    const newFolder: Folder = {
      ...folderData,
      id: `folder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date()
    };
    setFolders(prev => [...prev, newFolder]);
  };

  const updateFolder = (id: string, updates: Partial<Folder>) => {
    setFolders(prev => prev.map(folder =>
      folder.id === id ? { ...folder, ...updates } : folder
    ));
  };

  const deleteFolder = (id: string) => {
    // Move all notes from this folder to root
    setNotes(prev => prev.map(note =>
      note.folderId === id ? { ...note, folderId: undefined } : note
    ));
    setFolders(prev => prev.filter(folder => folder.id !== id));
  };

  const moveNoteToFolder = (noteId: string, folderId?: string) => {
    updateNote(noteId, { folderId });
  };

  const searchNotes = (query: string): Note[] => {
    if (!query.trim()) return notes;
    
    const searchTerm = query.toLowerCase();
    return notes.filter(note =>
      note.title.toLowerCase().includes(searchTerm) ||
      note.content.toLowerCase().includes(searchTerm) ||
      note.tags.some(tag => tag.toLowerCase().includes(searchTerm))
    );
  };

  const getNotesInFolder = (folderId?: string): Note[] => {
    return notes.filter(note => note.folderId === folderId);
  };

  const getFavoriteNotes = (): Note[] => {
    return notes.filter(note => note.isFavorite);
  };

  const getRecentNotes = (): Note[] => {
    return [...notes]
      .sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())
      .slice(0, 10);
  };

  return (
    <NotesManagerContext.Provider value={{
      notes,
      folders,
      addNote,
      updateNote,
      deleteNote,
      addFolder,
      updateFolder,
      deleteFolder,
      moveNoteToFolder,
      searchNotes,
      getNotesInFolder,
      getFavoriteNotes,
      getRecentNotes
    }}>
      {children}
    </NotesManagerContext.Provider>
  );
}

export const useNotesManager = () => {
  const context = useContext(NotesManagerContext);
  if (context === undefined) {
    throw new Error('useNotesManager must be used within a NotesManagerProvider');
  }
  return context;
};